import api from './api';

interface AnalyticsEvent {
  event_type: string;
  page?: string;
  post_id?: string;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

class AnalyticsTracker {
  private queue: AnalyticsEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private pageEnterTime: number = 0;
  private currentPage: string = '';

  start() {
    if (this.flushInterval) return;
    this.flushInterval = setInterval(() => this.flush(), 10000);
    window.addEventListener('beforeunload', this.handleUnload);
  }

  stop() {
    this.trackPageLeave();
    this.flush();
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    window.removeEventListener('beforeunload', this.handleUnload);
  }

  trackPageView(page: string) {
    this.trackPageLeave();
    this.currentPage = page;
    this.pageEnterTime = Date.now();
    this.queue.push({
      event_type: 'page_view',
      page,
      timestamp: new Date().toISOString(),
    });
    this.autoFlush();
  }

  trackPostView(postId: string, durationMs: number) {
    this.queue.push({
      event_type: 'post_view',
      post_id: postId,
      duration_ms: durationMs,
      page: this.currentPage,
      timestamp: new Date().toISOString(),
    });
    this.autoFlush();
  }

  trackEvent(eventType: string, metadata?: Record<string, unknown>) {
    this.queue.push({
      event_type: eventType,
      page: this.currentPage,
      metadata,
      timestamp: new Date().toISOString(),
    });
    this.autoFlush();
  }

  private trackPageLeave() {
    if (this.currentPage && this.pageEnterTime > 0) {
      const duration = Date.now() - this.pageEnterTime;
      if (duration > 500) {
        this.queue.push({
          event_type: 'page_view',
          page: this.currentPage,
          duration_ms: duration,
          timestamp: new Date(this.pageEnterTime).toISOString(),
        });
      }
    }
  }

  private handleUnload = () => {
    this.trackPageLeave();
    if (this.queue.length > 0) {
      const payload = JSON.stringify({ events: this.queue.splice(0, 50) });
      navigator.sendBeacon(
        `${api.defaults.baseURL}/analytics/events`,
        new Blob([payload], { type: 'application/json' })
      );
    }
  };

  private autoFlush() {
    if (this.queue.length >= 50) {
      this.flush();
    }
  }

  private async flush() {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, 50);
    try {
      await api.post('/analytics/events', { events: batch });
    } catch {
      // Put events back on failure (but don't let queue grow unbounded)
      if (this.queue.length < 200) {
        this.queue.unshift(...batch);
      }
    }
  }
}

export const analyticsTracker = new AnalyticsTracker();
